import { useState, useEffect, useRef } from "react"
import { router } from "@inertiajs/react"
import { Button } from "@ui/Button"
import { Dialog } from "@ui/Dialog"
import { Input } from "@ui/Input"
import { Spinner } from "@ui/Spinner"
import { Switch } from "@ui/Switch"
import { describeRecurrence, recurrenceEndDate } from "./formatters"
import MeetingsIcon from "../../icons/cisco-momentum/core/meetings-regular.svg?react"
import SearchIcon from "../../icons/cisco-momentum/core/search-regular.svg?react"
import RecurringIcon from "../../icons/cisco-momentum/core/recurring-regular.svg?react"
import LocationIcon from "../../icons/cisco-momentum/core/location-regular.svg?react"
import UserIcon from "../../icons/cisco-momentum/core/user-regular.svg?react"

function formatDate(dateStr, tz) {
  if (!dateStr) return ""
  const opts = { month: "short", day: "numeric", year: "numeric" }
  if (tz) opts.timeZone = tz
  return new Date(dateStr).toLocaleDateString("en-US", opts)
}

function formatTime(dateStr, tz) {
  if (!dateStr) return ""
  const opts = { hour: "numeric", minute: "2-digit" }
  if (tz) opts.timeZone = tz
  return new Date(dateStr).toLocaleTimeString("en-US", opts)
}

export default function AcademicCalendarEventModal({ isOpen, onClose, courseId, timezone }) {
  const [ searchTerm, setSearchTerm ] = useState("")
  const [ results, setResults ] = useState([])
  const [ searching, setSearching ] = useState(false)
  const [ selectedId, setSelectedId ] = useState(null)
  const [ error, setError ] = useState(null)
  const [ searchAll, setSearchAll ] = useState(false)
  const searchTimeout = useRef(null)
  const latestRequest = useRef(0)

  useEffect(() => {
    if (isOpen) {
      fetchEvents("", false)
    } else {
      clearTimeout(searchTimeout.current)
      latestRequest.current++
      setSearchTerm("")
      setResults([])
      setSelectedId(null)
      setError(null)
      setSearchAll(false)
    }
  }, [ isOpen ])

  async function fetchEvents(q, all) {
    const requestId = ++latestRequest.current
    setError(null)
    if (all && !q.trim()) {
      setResults([])
      setSearching(false)
      return
    }

    setSearching(true)
    const params = new URLSearchParams()
    if (all) params.set("scope", "all")
    if (q) params.set("q", q)
    const query = params.toString()
    try {
      const res = await fetch(`/lti/courses/${courseId}/calendar_events${query ? `?${query}` : ""}`)
      const data = await res.json()
      if (requestId === latestRequest.current) setResults(data.items || [])
    } catch {
      if (requestId === latestRequest.current) setError("Failed to load events")
    } finally {
      if (requestId === latestRequest.current) setSearching(false)
    }
  }

  function handleSearch(q) {
    setSearchTerm(q)
    setSelectedId(null)
    clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => {
      fetchEvents(q, searchAll)
    }, 300)
  }

  function handleSearchAllChange(checked) {
    setSearchAll(checked)
    setSelectedId(null)
    clearTimeout(searchTimeout.current)
    fetchEvents(searchTerm, checked)
  }

  function emptyMessage() {
    if (searchAll) return searchTerm.trim() ? "No matching events found" : "Search by course code or event name"
    return searchTerm ? "No matching events found" : "No academic events match this course"
  }

  function handleSelect() {
    if (!selectedId) return
    const item = results.find(r => r.id === selectedId)
    if (!item) return
    router.get(`/lti/courses/${courseId}/course_meetings/new`, {
      prefill_title: item.title || "",
      prefill_start: item.prefill_starts_at || item.starts_at || "",
      prefill_end: item.prefill_ends_at || item.ends_at || "",
      prefill_recurrence: item.recurrence_rule || "",
      prefill_space: item.location_ref || ""
    })
    onClose()
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={(o) => { if (!o) onClose() }}>
      <Dialog.Content size="md">
        <Dialog.Header>
          <Dialog.Title>Schedule from Academic Calendar</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          <div className="space-y-4">
            <Input
              type="text"
              value={searchTerm}
              onChange={e => handleSearch(e.target.value)}
              placeholder={searchAll ? "Search all academic events..." : "Search academic events..."}
              leadingIcon={<SearchIcon aria-hidden="true" />}
            />

            <Switch
              label="Search all current and upcoming events"
              checked={searchAll}
              onCheckedChange={handleSearchAllChange}
            />

            <div className="min-h-[256px] max-h-64 overflow-y-auto border border-webex-gray-300 dark:border-webex-gray-700 rounded-md relative">
              {searching && (
                <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/60 dark:bg-webex-gray-950/60">
                  <Spinner size="md" />
                </div>
              )}
              {results.length === 0 && !searching ? (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-webex-gray-600 dark:text-webex-gray-400 px-4 text-center">
                  {emptyMessage()}
                </div>
              ) : (
                <ul className="divide-y divide-webex-gray-200 dark:divide-webex-gray-800">
                  {results.map(event => {
                    const isSelected = selectedId === event.id
                    const recurrence = event.is_recurring ? describeRecurrence(event.recurrence_rule) : null
                    const endDate = event.is_recurring ? recurrenceEndDate(event.recurrence_rule) : null
                    const dateRangeLabel = event.is_recurring
                      ? `${formatDate(event.starts_at, timezone)} - ${endDate ? formatDate(endDate, timezone) : formatDate(event.ends_at, timezone) || "Ongoing"}`
                      : formatDate(event.starts_at, timezone)
                    return (
                      <li key={event.id}>
                        <button
                          type="button"
                          onClick={() => setSelectedId(event.id)}
                          className={`w-full px-4 py-3.5 text-left transition-colors motion-reduce:transition-none ${
                            isSelected
                              ? "bg-webex-blue-100 dark:bg-webex-blue-900"
                              : "hover:bg-webex-gray-50 dark:hover:bg-webex-gray-900"
                          }`}
                        >
                          <div className={`text-sm flex items-center gap-2 ${
                            isSelected ? "text-webex-blue-700 dark:text-webex-blue-400" : "text-webex-gray-1000 dark:text-white"
                          }`}>
                            <MeetingsIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
                            <span className="truncate">{event.title}</span>
                          </div>
                          {event.starts_at && (
                            <div className="mt-1.5 ml-6 flex items-center gap-2">
                              <span className={`text-sm font-semibold ${
                                isSelected ? "text-webex-blue-700 dark:text-webex-blue-400" : "text-webex-gray-1000 dark:text-white"
                              }`}>
                                {formatTime(event.starts_at, timezone)}
                                {event.ends_at && ` - ${formatTime(event.ends_at, timezone)}`}
                              </span>
                              {recurrence && (
                                <span className={`inline-flex items-center gap-1 text-sm ${
                                  isSelected ? "text-webex-blue-700 dark:text-webex-blue-400" : "text-webex-gray-600 dark:text-webex-gray-400"
                                }`}>
                                  <RecurringIcon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                                  {recurrence.prefix}
                                  {recurrence.days && (
                                    <span className={`font-semibold ${
                                      isSelected ? "text-webex-blue-700 dark:text-webex-blue-400" : "text-webex-gray-1000 dark:text-white"
                                    }`}>
                                      {recurrence.days}
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                          )}
                          <div className={`text-xs mt-1 ml-6 ${
                            isSelected ? "text-webex-blue-700 dark:text-webex-blue-400" : "text-webex-gray-600 dark:text-webex-gray-400"
                          }`}>
                            {dateRangeLabel}
                          </div>
                          {(event.instructor || event.location) && (
                            <div className={`text-xs mt-1 ml-6 flex items-center gap-2 ${
                              isSelected ? "text-webex-blue-700 dark:text-webex-blue-400" : "text-webex-gray-600 dark:text-webex-gray-400"
                            }`}>
                              {event.instructor && (
                                <span className="inline-flex items-center gap-1">
                                  <UserIcon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                                  {event.instructor}
                                </span>
                              )}
                              {event.location && (
                                <span className="ml-auto shrink-0 inline-flex items-center gap-1">
                                  <LocationIcon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                                  {event.location}
                                </span>
                              )}
                            </div>
                          )}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>

            {error && <p className="text-xs text-webex-red-700 dark:text-webex-red-400">{error}</p>}

            {results.length > 0 && (
              <p className="text-xs text-webex-gray-600 dark:text-webex-gray-400">
                {results.length} event{results.length === 1 ? "" : "s"}
              </p>
            )}
          </div>
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleSelect} disabled={!selectedId}>
            Schedule
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}
